import { PageSlice } from '@contracts';
import React from 'react';
import styled from 'styled-components';
import Carousel from './Carousel';
import Colors from './Colors';
import Trustpilot from './Trustpilot';
import VideoWithThumbnail from './VideoWithThumbnail';
import useDesktop from './useDesktop';

const carouselOptionsMobile = {
  autoPlay: false,
  groupCells: false,
  cellAlign: 'center',
  draggable: true,
  pageDots: true,
  prevNextButtons: false,
  watchCSS: false
};

const carouselOptionsDesktop = {
  autoPlay: false,
  groupCells: true,
  cellAlign: 'center',
  draggable: false,
  pageDots: false,
  prevNextButtons: true,
  watchCSS: false
};

interface Props {
  slice: PageSlice;
}

const VideoGrid: React.FC<Props> = ({ slice }) => {
  const isDesktop = useDesktop(1024);
  const { primary, items } = slice;
  const { grid_title, title_text_color, trustpilot, frame, scroll_elements_desktop, text_align_all, rounded_corners } = primary;
  const gap = (isDesktop ? primary?.gap?.text : primary?.gap_mobile?.text || primary?.gap?.text) || '0';
  const hasDesktopCarousel = scroll_elements_desktop !== 'none';
  const carouselOptions = isDesktop ? carouselOptionsDesktop : carouselOptionsMobile;

  return (
    <Container $color={title_text_color}>
      {trustpilot && (
        <TrustpilotWrap>
          <Trustpilot justifyContent="start" showFallback={false} />
        </TrustpilotWrap>
      )}
      <CarouselWrap $gap={gap}>
        {grid_title.text && <b dangerouslySetInnerHTML={{ __html: grid_title.text }} />}
        {isDesktop && !hasDesktopCarousel ? (
          <RowWrap>
            {items &&
              items.map((item, index) => (
                <VideoWrap key={index}>
                  <VideoWithThumbnail
                    video={item}
                    hasFrame={frame}
                    textColor={title_text_color}
                    textAlign={text_align_all}
                    roundedCorners={rounded_corners}
                  />
                </VideoWrap>
              ))}
          </RowWrap>
        ) : (
          <Carousel options={carouselOptions}>
            {items &&
              items.map((item, index) => (
                <VideoWrap $hasAdditionalPadding={true} key={index}>
                  <VideoWithThumbnail video={item} hasFrame={frame} gap={gap} textColor={title_text_color} />
                </VideoWrap>
              ))}
          </Carousel>
        )}
      </CarouselWrap>
    </Container>
  );
};

export default VideoGrid;

interface IContainer {
  $color: string;
}

interface ICarouselWrap {
  $gap: string;
}

interface IVideoWrap {
  $hasAdditionalPadding?: boolean;
}

const Container = styled.div<IContainer>`
  display: grid;
  padding: 0 0 120px 0;
  b {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 50px;
    line-height: 110%;
    display: flex;
    align-items: flex-end;
    text-align: center;
    justify-content: center;
    color: ${(props) => (props.$color ? props.$color : Colors.forestGreen)};
  }

  @media (max-width: 768px) {
    padding: 0 0 60px 0;

    b {
      padding: 0 30px;
      font-size: 24px;
      line-height: 120%;
    }
  }
`;

const TrustpilotWrap = styled.div`
  justify-self: center;
  width: 300px;
  margin-left: -15px;
  margin-bottom: 23px;

  @media (max-width: 768px) {
    display: flex;
    max-height: 40px;
    align-items: center;
  }
`;

const RowWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  position: relative;
`;

const CarouselWrap = styled.div<ICarouselWrap>`
  display: grid;
  gap: ${(props) => props.$gap};
  width: 100%;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const VideoWrap = styled.div<IVideoWrap>`
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;
